/**
 * MessagingAccessButton — ponto de acesso global à mensageria interna na topbar
 * (Mensageria Condensada · superfície 1 · PR F1a).
 *
 * Ícone próprio (≠ sino — pessoas, não sistema) com badge de não-lido AO VIVO,
 * reaproveitando o `useUnreadCount` que respira via WS já montado no AppShell
 * (`useDirectMessagesWsSync`) — SEM polling. Clicar abre o `CondensedMessagingPopover`
 * ancorado, sem scrim.
 *
 * P19 (gate silencioso): sem a cap `direct-message-participate`, o ícone INTEIRO
 * não existe (ausente, não desabilitado) — variante Solo remove tudo. O hook de
 * não-lido roda incondicionalmente (regras de hooks; ele já é self-gated no fetch).
 */

import { MessageSquareMore } from 'lucide-react';
import { useRef, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { useUnreadCount } from '../hooks/use-direct-messages';

import { CondensedMessagingPopover } from './CondensedMessagingPopover';

import './condensed.css';

function badgeLabel(unread: number): string {
  return unread > 9 ? '9+' : unread.toString();
}

export function MessagingAccessButton(): JSX.Element | null {
  const canParticipate = useCapability('direct-message-participate');
  const { unread } = useUnreadCount();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  // P19 — capability check silencioso: sem participate, mensageria não existe.
  if (!canParticipate) return null;

  return (
    <div className="mc-anchor">
      <button
        ref={btnRef}
        type="button"
        className={`mc-access-btn${open ? ' is-open' : ''}`}
        aria-label="Mensagens internas"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <MessageSquareMore size={18} strokeWidth={1.5} aria-hidden />
        {unread > 0 && (
          <span
            className="mc-access-badge"
            data-testid="mc-access-badge"
            aria-label={`${unread.toString()} não lidas`}
          >
            {badgeLabel(unread)}
          </span>
        )}
      </button>

      {open && (
        <CondensedMessagingPopover
          anchorRef={btnRef}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}
